import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LoadingSkeleton } from './components/LoadingSkeleton';
import { ProjectScopedRedirect } from './components/ProjectScopedRedirect';

// Every route is lazy-loaded so each page becomes its own chunk and the
// initial bundle stays small.
//
// Authentication note: the root `/` redirects to `/projects` (the post-login
// landing per spec/00-INDEX.md). Auth gating that bounces unauthenticated
// users to `/login` is performed by AuthGuard at a higher level — the router
// here stays auth-agnostic.

const LoginPage = lazy(() => import('./pages/LoginPage'));

// Path A / flat project pages (kept — every page in spec/00 §0.7 nav).
const CsdbPage = lazy(() => import('./pages/CsdbPage'));
const ImportPage = lazy(() => import('./pages/ImportPage'));
const GraphicsPage = lazy(() => import('./pages/GraphicsPage'));
const ApplicabilityPage = lazy(() => import('./pages/ApplicabilityPage'));
// DMRL-1 Phase 2 — plan-first DM list. Separate from CSDB; the
// sidebar entry is SU-gated until the API gate is broadened.
const DmrlPlanningPage = lazy(() => import('./pages/dmrl/DmrlPlanningPage'));
const PublishingPage = lazy(() => import('./pages/publishing/PublishingPage'));
const ReviewPage = lazy(() => import('./pages/ReviewPage'));
const ManualsPage = lazy(() => import('./pages/ManualsPage'));
const AiPage = lazy(() => import('./pages/AiPage'));

// Projects.
const ProjectListPage = lazy(() => import('./pages/ProjectListPage'));
const ProjectSettingsPage = lazy(() => import('./pages/ProjectSettingsPage'));
const ProjectUsersPage = lazy(() => import('./pages/ProjectUsersPage'));
const ProjectReviewPage = lazy(() => import('./pages/ProjectReviewPage'));
const ProjectManualsReviewPage = lazy(() => import('./pages/ProjectManualsReviewPage'));
const ProjectManualsPage = lazy(() => import('./pages/ProjectManualsPage'));
const ManualWorkspacePage = lazy(() => import('./pages/ManualWorkspacePage'));
const ProjectAiPage = lazy(() => import('./pages/ProjectAiPage'));
const ProjectAiGenerateWizardPage = lazy(() => import('./pages/ProjectAiGenerateWizardPage'));
const ProjectDashboardPage = lazy(() => import('./pages/ProjectDashboardPage'));

// Profile.
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ProfileSecurityPage = lazy(() => import('./pages/ProfileSecurityPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));

// Admin (8 sub-pages — replaces the previous /admin/* catchall placeholder).
const AdminLandingPage = lazy(() => import('./pages/AdminLandingPage'));
const ManageUsersPage = lazy(() => import('./pages/ManageUsersPage'));
const CageCodesPage = lazy(() => import('./pages/CageCodesPage'));
const RolesPage = lazy(() => import('./pages/RolesPage'));
const S1000DConfigPage = lazy(() => import('./pages/S1000DConfigPage'));
const OrgSettingsPage = lazy(() => import('./pages/OrgSettingsPage'));
const UserAccessSecurityPage = lazy(() => import('./pages/UserAccessSecurityPage'));
const AiProviderSettingsPage = lazy(() => import('./pages/AiProviderSettingsPage'));
const HelpCentrePage = lazy(() => import('./pages/HelpCentrePage'));
const EquipmentCataloguePage = lazy(() => import('./pages/EquipmentCataloguePage'));

// Help (top-level — distinct from the admin Help Centre).
const HelpPage = lazy(() => import('./pages/HelpPage'));

const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const BulkImportStatusPage = lazy(() => import('./pages/BulkImportStatusPage'));
const DmEditorPage = lazy(() => import('./pages/DmEditorPage'));

function RouteFallback() {
  // Spec §0.11 rule #4: never show a blank loading state — always skeletons.
  return (
    <div className="ta-route-fallback">
      <LoadingSkeleton variant="text" lines={6} />
    </div>
  );
}

export function AppRouter() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Root → /projects (post-login landing per spec/00-INDEX.md). */}
        <Route path="/" element={<Navigate to="/projects" replace />} />

        <Route path="/login" element={<LoginPage />} />
        {/* `/dashboard` was the global dashboard before the Home/per-
            project-dashboard split. Preserved as a redirect so old
            bookmarks land on Home. */}
        <Route path="/dashboard" element={<Navigate to="/projects" replace />} />

        {/* Flat path-A pages (spec §0.7 nav). Pages like CSDB/Import/
            Graphics/Applicability/Publishing read `:id` from the URL —
            visiting them flat without project context used to silently
            render a broken shell. Each now routes through
            `ProjectScopedRedirect`, which either Navigate-replaces to
            `/projects/:id/<segment>` when the active project matches
            the page's required pathType or renders an honest "open a
            project first" empty state. `/review`, `/ai`, and `/manuals`
            stay flat because their flat-page components are real
            picker/landing surfaces, not broken-without-context pages. */}
        <Route
          path="/csdb"
          element={<ProjectScopedRedirect segment="csdb" label="CSDB" pathType="s1000d" />}
        />
        <Route
          path="/import"
          element={<ProjectScopedRedirect segment="import" label="Import" pathType="s1000d" />}
        />
        <Route
          path="/graphics"
          element={
            <ProjectScopedRedirect segment="graphics" label="Graphics & ICN" pathType="s1000d" />
          }
        />
        <Route
          path="/applicability"
          element={
            <ProjectScopedRedirect
              segment="applicability"
              label="Applicability & CIR"
              pathType="s1000d"
            />
          }
        />
        <Route
          path="/publishing"
          element={
            <ProjectScopedRedirect segment="publishing" label="Publishing" pathType="s1000d" />
          }
        />
        <Route path="/review" element={<ReviewPage />} />
        <Route path="/manuals" element={<ManualsPage />} />
        <Route path="/ai" element={<AiPage />} />

        {/* Projects — list + per-project pages. Param standardised on `:id`. */}
        <Route path="/projects" element={<ProjectListPage />} />
        <Route path="/projects/:id/dashboard" element={<ProjectDashboardPage />} />
        <Route path="/projects/:id/settings" element={<ProjectSettingsPage />} />
        <Route path="/projects/:id/users" element={<ProjectUsersPage />} />
        {/* Spec/09 — Path A M07 Review & Changes. Distinct page component
            from the Path B manuals review at /projects/:id/manuals/review. */}
        <Route path="/projects/:id/review" element={<ProjectReviewPage />} />
        <Route path="/projects/:id/manuals" element={<ProjectManualsPage />} />
        {/* Spec/08 §8.7 — Path B Review & Change Management; its own page,
            not the same component as Path A above. */}
        <Route path="/projects/:id/manuals/review" element={<ProjectManualsReviewPage />} />
        <Route path="/projects/:id/manuals/:manualId" element={<ManualWorkspacePage />} />
        <Route path="/projects/:id/ai" element={<ProjectAiPage />} />
        {/* Spec/10 §10.3 — AI Generate Wizard (full-page, 5 steps). */}
        <Route
          path="/projects/:id/ai/generate"
          element={<ProjectAiGenerateWizardPage />}
        />
        <Route path="/projects/:id/csdb" element={<CsdbPage />} />
        {/* Spec/07 §7.1 — Publishing / PM Builder. */}
        <Route path="/projects/:id/publishing" element={<PublishingPage />} />
        {/* Spec/04 §4.7 — single-DM editor surface, deep-linkable. */}
        <Route path="/projects/:id/csdb/editor/:dmId" element={<DmEditorPage />} />
        <Route path="/projects/:id/graphics" element={<GraphicsPage />} />
        <Route path="/projects/:id/applicability" element={<ApplicabilityPage />} />
        <Route path="/projects/:id/dmrl" element={<DmrlPlanningPage />} />
        <Route path="/projects/:id/import" element={<ImportPage />} />
        <Route path="/projects/:id/import/bulk/:jobId" element={<BulkImportStatusPage />} />

        {/* Profile — overview + security tab as a routed sibling. */}
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/security" element={<ProfileSecurityPage />} />

        {/* Admin — /admin is now a real landing hub listing the eight
            admin sub-pages (replacing the previous Navigate redirect to
            /admin/users so the surface is discoverable). Each sub-page
            still enforces its own auth + permission at the backend. */}
        <Route path="/admin" element={<AdminLandingPage />} />
        <Route path="/admin/users" element={<ManageUsersPage />} />
        <Route path="/admin/cage-codes" element={<CageCodesPage />} />
        <Route path="/admin/roles" element={<RolesPage />} />
        <Route path="/admin/s1000d" element={<S1000DConfigPage />} />
        <Route path="/admin/org" element={<OrgSettingsPage />} />
        <Route path="/admin/security" element={<UserAccessSecurityPage />} />
        <Route path="/admin/ai" element={<AiProviderSettingsPage />} />
        <Route path="/admin/help" element={<HelpCentrePage />} />
        {/* Phase 2D — Path B Equipment Catalogue admin page. SU-only
            at the frontend; backend also enforces SU on every route
            under `/api/v1/admin/equipment`. */}
        <Route path="/admin/equipment" element={<EquipmentCataloguePage />} />

        <Route path="/help" element={<HelpPage />} />

        {/* WS-4a (C2) — global search results page. */}
        <Route path="/search" element={<SearchPage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
